import { useTranslation } from 'react-i18next';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { ApiError, ApiTimeoutError } from '@/lib/api';

/**
 * Translates a thrown API error into a message for the user. The server's
 * stable `code` is what gets translated; its English `message` is never
 * shown, so the UI stays fully Arabic without the backend owning any copy.
 */
export function useApiError() {
  const { t } = useTranslation('errors');

  return (error: unknown): { message: string } => {
    // Rule E8: the request may already have succeeded server-side - this is
    // never worded like a confirmed failure, and it never invites a blind
    // retry of a consequential mutation.
    if (error instanceof ApiTimeoutError) return { message: t('timeout') };
    if (error instanceof ApiError) {
      return { message: t(error.code, { defaultValue: t('INTERNAL_ERROR') }) };
    }
    return { message: t('network') };
  };
}

interface ApiFieldError {
  path?: unknown;
  message?: unknown;
}

/**
 * Maps `error.details.fieldErrors` (e.g. a duplicate username or branch code
 * — `UNIQUE_VIOLATION` from `prisma-error.mapper.ts`) onto the matching
 * react-hook-form field, so the drawer form itself shows the problem instead
 * of only a generic toast. The field message is the same translated `code`
 * `useApiError` would show, not the server's raw English per-field text
 * (`"already taken"`), keeping the no-backend-copy rule.
 */
export function useApplyFieldErrors() {
  const { t } = useTranslation('errors');

  return <T extends FieldValues>(form: UseFormReturn<T>, error: unknown): boolean => {
    if (!(error instanceof ApiError)) return false;
    const fieldErrors = error.details['fieldErrors'];
    if (!Array.isArray(fieldErrors)) return false;

    const message = t(error.code, { defaultValue: t('INTERNAL_ERROR') });
    let applied = false;
    for (const entry of fieldErrors as ApiFieldError[]) {
      const path = entry?.path;
      if (typeof path !== 'string' || path.length === 0) continue;
      form.setError(path as Path<T>, { type: 'server', message });
      applied = true;
    }
    return applied;
  };
}
